/**
 * Purchase Orders: what we have ORDERED from a supplier.
 *
 * WHAT A PURCHASE ORDER IS NOT
 * ----------------------------
 * A purchase order is an INTENTION TO BUY. Issuing one says the shop has committed
 * to a supplier and expects the goods; it does NOT say the goods arrived, were
 * accepted, or were paid for. So, deliberately:
 *
 *  - **no stock movement.** `PURCHASE_ORDER.movesStock` is already `false` in
 *    `doctypes.ts`, and nothing in this service overrides it. Stock arrives when a
 *    purchase receipt is issued in a later milestone. Booking stock on the ORDER
 *    would let the shop sell goods it has not paid for and has not received, and
 *    would make the receipt double-count it.
 *  - **no supplier payable, no balance, no payment.** A supplier owes us nothing
 *    and we owe nothing until a purchase invoice exists. Recording a payable here
 *    would create a liability for goods that may never arrive.
 *  - **no conversion target.** `convertibleFrom` is empty, and `PO -> Purchase
 *    Invoice` belongs to a future milestone. Nothing here implements it.
 *
 * WHY A SEPARATE SERVICE AND NOT A PARAMETERISED ONE
 * --------------------------------------------------
 * `salesOrderService` could have been made `side`-aware, and that is the obvious
 * "no duplication" move. It would also have meant touching a frozen, fully-tested
 * sales path to add a branch that sales never takes. The two differ in the party
 * they read (`supplierId` and `kind: 'SUPPLIER'`) and in which direction place of
 * supply is resolved — for a sale it is where the goods are DELIVERED, for a
 * purchase it is where the goods COME FROM. That is a different business fact, not
 * a flag, and encoding it as a flag would hide which rule applied. The shared
 * machinery is already factored out where it belongs: `issueDocument`,
 * `computeDocument`, `toPartySnapshot`, and the sequences.
 *
 * THE TAX DIRECTION
 * -----------------
 * `splitGst` is not sales-specific. Given a place of supply it produces IGST when
 * that state differs from the business's and CGST+SGST when it matches — which is
 * exactly the purchase rule, because tax is charged where the SUPPLY originates.
 * For a purchase the supply originates at the SUPPLIER, so `placeOfSupplyCode` is
 * the supplier's state. Getting this backwards would apply CGST+SGST to goods
 * bought across a state border.
 *
 * A supplier with NO state is NOT defaulted to the business state. The resolver
 * reports `UNRESOLVED`, the document records an empty place of supply, and the tax
 * is computed as intra-state — the same "no silent fallback" rule already
 * established for master data and the POS. Inventing a state here would quietly
 * apply the wrong tax to every purchase from that supplier, forever.
 */
import type { DNRDatabase } from '@data/db/database';
import type { Document as StoredDocument } from '@data/db/types';
import { currentDeviceId } from '@data/sync/deviceIdentity';
import { nextVersion, recordBusinessMutations } from '@data/sync/recordBusinessMutation';
import { ulid } from '@platform/ids';
import { issueDocument, type IssueParams } from './documentService';
import { toPartySnapshot } from './partySnapshot';
import { listParties } from './partyReader';
import { listProducts } from './productReader';

export class PurchaseOrderServiceError extends Error {
  constructor(
    readonly code:
      | 'NO_BUSINESS'
      | 'NOT_FOUND'
      | 'NOT_EDITABLE'
      | 'PARTY_NOT_FOUND'
      | 'NOT_A_SUPPLIER'
      | 'NO_LINES'
      | 'NO_ITEM'
      | 'WRONG_BUSINESS'
      | 'INVALID_INPUT'
      | 'HAS_CHILDREN',
    message: string,
  ) {
    super(message);
    this.name = 'PurchaseOrderServiceError';
  }
}

export interface PurchaseOrderLineInput {
  readonly productId: string;
  readonly quantityMilli: number;
  /**
   * The rate typed on THIS order, in paise.
   *
   * Never overwritten from `product.salePricePaise` or `purchasePricePaise`. What a
   * shop agreed with a particular supplier on a particular day is a fact about the
   * order, and copying the current catalogue price over it would make a saved
   * purchase order change its own total when the catalogue is later edited.
   */
  readonly ratePaise: number;
  readonly description?: string;
  /** Unit of measure for this line (e.g., NOS, SQFT, KG). Defaults to product's unit. */
  readonly unit?: string;
}

export interface PurchaseOrderInput {
  readonly businessId: string;
  readonly supplierId: string;
  readonly docDate: string;
  readonly lines: readonly PurchaseOrderLineInput[];
  readonly roundOff?: boolean;
  readonly notes?: string;
  readonly terms?: string;
  /** Explicit supply state. Defaults to the SUPPLIER's state, never to the business. */
  readonly placeOfSupplyOverride?: string;
  readonly now?: string;
  readonly deviceId?: string;
}

/** Suppliers and products for the editor's pickers. */
export async function purchaseOrderFormOptions(db: DNRDatabase, businessId: string) {
  const [suppliers, products] = await Promise.all([
    listParties(db as never, businessId, { kind: 'SUPPLIER' }),
    listProducts(db as never, businessId, { activeOnly: true }),
  ]);
  return { suppliers, products };
}

/**
 * A real calendar date, checked by round-tripping it.
 *
 * `new Date('2026-02-31')` is the 3rd of March, not an error, so a regex alone would
 * accept a date the user never meant.
 */
function assertRealDate(value: string): void {
  const parsed = new Date(`${value}T00:00:00Z`);
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(value) ||
    Number.isNaN(parsed.getTime()) ||
    parsed.toISOString().slice(0, 10) !== value
  ) {
    throw new PurchaseOrderServiceError('INVALID_INPUT', 'Enter a valid purchase order date.');
  }
}

async function buildPurchaseOrder(
  db: DNRDatabase,
  input: PurchaseOrderInput,
  identity?: StoredDocument,
) {
  if (!input.lines.length) {
    throw new PurchaseOrderServiceError('NO_LINES', 'A purchase order needs at least one line.');
  }
  assertRealDate(input.docDate);
  for (const line of input.lines) {
    if (
      !Number.isSafeInteger(line.quantityMilli) ||
      line.quantityMilli <= 0 ||
      !Number.isSafeInteger(line.ratePaise) ||
      line.ratePaise < 0
    ) {
      throw new PurchaseOrderServiceError(
        'INVALID_INPUT',
        'Each order line needs a positive quantity and a non-negative rate.',
      );
    }
  }

  const business = await db.businesses.get(input.businessId);
  if (!business) {
    throw new PurchaseOrderServiceError('NO_BUSINESS', 'Select a business before creating a purchase order.');
  }

  const supplier = await db.parties.get(input.supplierId);
  if (!supplier) {
    throw new PurchaseOrderServiceError('PARTY_NOT_FOUND', 'Choose a valid supplier.');
  }
  // The role is checked, not assumed. A CUSTOMER row pasted into this field would
  // otherwise produce a purchase order against the wrong counterparty, with the
  // wrong state and therefore the wrong tax split.
  if (supplier.kind !== 'SUPPLIER') {
    throw new PurchaseOrderServiceError(
      'NOT_A_SUPPLIER',
      'That contact is a customer, not a supplier. Add them as a supplier first.',
    );
  }
  if (supplier.businessId !== input.businessId) {
    throw new PurchaseOrderServiceError('WRONG_BUSINESS', 'That supplier belongs to another business.');
  }

  const products = await Promise.all(
    input.lines.map(async (line) => {
      const product = await db.products.get(line.productId);
      if (!product || product.businessId !== input.businessId) {
        throw new PurchaseOrderServiceError('NO_ITEM', 'A selected product is unavailable.');
      }
      const tax = product.taxRateId ? await db.taxRates.get(product.taxRateId) : undefined;
      if (tax && tax.businessId !== input.businessId) {
        throw new PurchaseOrderServiceError(
          'NO_ITEM',
          'A selected product tax rate belongs to another business.',
        );
      }
      return { line, product, tax };
    }),
  );

  const now = input.now ?? new Date().toISOString();
  const deviceId = input.deviceId ?? (await currentDeviceId(db));
  const existing = identity ? await db.documents.get(identity.id) : undefined;
  if (identity && (!existing || existing.docType !== 'PURCHASE_ORDER' || existing.status !== 'DRAFT')) {
    throw new PurchaseOrderServiceError('NOT_EDITABLE', 'Only purchase order drafts can be edited.');
  }
  if (existing && existing.businessId !== input.businessId) {
    throw new PurchaseOrderServiceError('WRONG_BUSINESS', 'The purchase order belongs to another business.');
  }

  // THE TAX DIRECTION, in one place. The SUPPLIER's state, never the business's.
  const placeOfSupply = input.placeOfSupplyOverride || supplier.stateCode || '';

  const params: IssueParams = {
    businessId: input.businessId,
    businessStateCode: business.stateCode,
    docType: 'PURCHASE_ORDER',
    docDate: input.docDate,
    supplierId: supplier.id,
    partySnapshot: toPartySnapshot(supplier),
    placeOfSupplyCode: placeOfSupply,
    // Where the goods must be DELIVERED to us — the shop's own address, which is
    // the business profile, not the supplier's. Confusing the two would print the
    // supplier's address in our "deliver to" block and hide ours.
    deliveryAddressLines: business.addressLines ? [...business.addressLines] : undefined,
    deliveryStateCode: business.stateCode,
    deliveryStateName: business.stateName,
    lines: products.map(({ line, product, tax }) => ({
      productId: product.id,
      description: line.description?.trim() || product.name,
      hsnSac: product.hsnSac,
      unit: line.unit ?? product.unit,
      quantityMilli: line.quantityMilli,
      // The rate typed on this order. See `PurchaseOrderLineInput.ratePaise`.
      ratePaise: line.ratePaise,
      taxRateBp: tax?.rateBp ?? 0,
      cessRateBp: product.cessRateBp,
      isStockTracked: product.isStockTracked,
    })),
    notes: input.notes,
    terms: input.terms,
    roundOff: input.roundOff ?? false,
    status: 'DRAFT',
    ...(existing ? { replaceIdentity: { id: existing.id, docNumber: existing.docNumber } } : {}),
    user: { id: 'local', name: 'You' },
    now,
    deviceId,
    fyStartMonth: business.fyStartMonth,
  };

  const result = await db.transaction(
    'rw',
    [
      db.documents,
      db.documentLines,
      db.documentLinks,
      db.stockTransactions,
      db.documentSequences,
      db.auditLogs,
      db.parties,
      db.products,
      db.businesses,
      db.syncOutbox,
    ],
    () => issueDocument(db as never, params),
  );

  return { id: result.id, docNumber: result.docNumber, grandTotalPaise: result.grandTotalPaise };
}

/** Create a purchase order DRAFT. No stock, no payable, nothing beyond the draft. */
export function createPurchaseOrderDraft(db: DNRDatabase, input: PurchaseOrderInput) {
  return buildPurchaseOrder(db, input);
}

/** Edit a draft in place, keeping its id and its allocated number. */
export async function updatePurchaseOrderDraft(
  db: DNRDatabase,
  id: string,
  input: Omit<PurchaseOrderInput, 'businessId'>,
) {
  const existing = await db.documents.get(id);
  if (!existing || existing.docType !== 'PURCHASE_ORDER') {
    throw new PurchaseOrderServiceError('NOT_FOUND', 'Purchase order not found.');
  }
  if (existing.status !== 'DRAFT') {
    throw new PurchaseOrderServiceError('NOT_EDITABLE', 'Issued purchase orders are immutable.');
  }
  return buildPurchaseOrder(
    db,
    {
      ...input,
      businessId: existing.businessId,
      // A draft's place of supply is a fact about this order, so it survives an
      // edit that does not mention it. Falling back to a freshly-read supplier
      // state would silently re-tax the order when the supplier moved.
      placeOfSupplyOverride: input.placeOfSupplyOverride ?? existing.placeOfSupplyCode,
    },
    existing,
  );
}

/**
 * Delete a draft. An ISSUED order is never deleted — it is the record of a
 * commitment made to a supplier, and it is the only thing that proves what was
 * ordered when the goods fail to arrive.
 */
export async function deletePurchaseOrderDraft(
  db: DNRDatabase,
  id: string,
  input: { now?: string; deviceId?: string } = {},
) {
  const now = input.now ?? new Date().toISOString();
  const deviceId = input.deviceId ?? (await currentDeviceId(db));
  return db.transaction(
    'rw',
    [db.documents, db.documentLines, db.documentLinks, db.auditLogs, db.syncOutbox],
    async () => {
      const order = await db.documents.get(id);
      if (!order || order.docType !== 'PURCHASE_ORDER') {
        throw new PurchaseOrderServiceError('NOT_FOUND', 'Purchase order not found.');
      }
      if (order.status !== 'DRAFT') {
        throw new PurchaseOrderServiceError('NOT_EDITABLE', 'Only a draft purchase order can be deleted.');
      }
      const children = await db.documentLinks.where('toDocumentId').equals(id).toArray();
      if (children.some((link) => link.fromDocumentId !== id)) {
        throw new PurchaseOrderServiceError(
          'HAS_CHILDREN',
          'This purchase order is referenced by another document and cannot be deleted.',
        );
      }

      const lines = await db.documentLines.where('documentId').equals(id).toArray();
      const links = await db.documentLinks.where('fromDocumentId').equals(id).toArray();
      await db.documentLines.where('documentId').equals(id).delete();
      await db.documentLinks.where('fromDocumentId').equals(id).delete();
      await db.documents.delete(id);
      await db.auditLogs.add({
        id: ulid(),
        businessId: order.businessId,
        entityType: 'DOCUMENT',
        entityId: id,
        action: 'DELETE',
        ...(order.docNumber ? { description: `Deleted purchase order draft ${order.docNumber}` } : {}),
        actorId: 'local',
        createdAt: now,
      } as never);

      // A TOMBSTONE FOR EVERY ROW REMOVED, not just the document.
      //
      // Deleting the rows locally without publishing this leaves the draft alive on
      // every other device: the change log never says it was removed, so a peer that
      // already had it — or that receives an older create afterwards — keeps showing
      // a purchase order the user deleted. It is the same reason `deleteParty` and
      // `deleteProduct` tombstone rather than drop rows.
      await recordBusinessMutations(db as never, [
        {
          businessId: order.businessId,
          deviceId,
          entityType: 'document',
          entityId: id,
          operation: 'DELETE',
          version: nextVersion(order.syncVersion),
          changedAt: now,
          deletedAt: now,
        },
        ...lines.map((line) => ({
          businessId: line.businessId,
          deviceId,
          entityType: 'documentLine' as const,
          entityId: line.id,
          operation: 'DELETE' as const,
          version: nextVersion(line.syncVersion ?? 1),
          changedAt: now,
          deletedAt: now,
        })),
        ...links.map((link) => ({
          businessId: link.businessId,
          deviceId,
          entityType: 'documentLink' as const,
          entityId: link.id,
          operation: 'DELETE' as const,
          version: nextVersion(link.syncVersion ?? 1),
          changedAt: now,
          deletedAt: now,
        })),
      ]);

      return { id, deletedLines: lines.length, deletedLinks: links.length };
    },
  );
}